import { every, find, isFinite, map, forEach, size, noop, get, chain, capitalize, toUpper, isEmpty } from "lodash"
import { flattenClassificationValues } from "../classification"
import { VIEW_TYPES } from "helpers/base"
import { NORMALIZATION } from "helpers/segments"

/* Query formatters */
function fixedValueFormatter(value) {
  return (id, operator) => ({ [operator]: value })
}

function querySingleFloatFormatter(id, operator, dataType, ...values) {
  let value = parseFloat(values[0])
  if (dataType === "millions") {
    value *= 1000000
  }
  return {
    [operator]: value,
  }
}

function queryRangeFloatFormatter(id, operator, dataType, ...values) {
  let min = parseFloat(values[0])
  let max = parseFloat(values[1])
  if (dataType === "millions") {
    min *= 1000000
    max *= 1000000
  }
  return {
    [operator]: { min, max },
  }
}

function querySingleStringFormatter(id, operator, dataType, ...values) {
  return {
    [operator]: values[0].value,
  }
}

function queryMultiStringFormatter(id, operator, dataType, ...values) {
  return {
    [operator]: map(values, ({ value }) => value),
  }
}

function querySingleClassificationFormatter(id, operator, dataType, ...values) {
  return {
    [operator]: { tier: values[0].tierId, value: values[0].value },
  }
}

function queryMultiClassificationFormatter(id, operator, dataType, ...values) {
  return {
    [operator]: map(values, ({ tierId, value }) => ({ tier: tierId, value })),
  }
}

function querySingleMembershipFormatter(id, operator, dataType, ...values) {
  return {
    [operator]: values[0].value,
  }
}

function queryMultiMembershipFormatter(id, operator, dataType, ...values) {
  return {
    [operator]: map(values, ({ value }) => value),
  }
}

/* Validators */
function alwaysValid() {
  return true
}

function singleNumberValidator(values) {
  return isFinite(parseFloat(values[0]))
}

function rangeNumberValidator(values) {
  return isFinite(parseFloat(values[0])) && isFinite(parseFloat(values[1]))
}

function descriptorValidator(values) {
  return !!values[0]
}

function securityValidator(values) {
  if (isEmpty(values)) {
    return false
  }
  return every(values, v => v.indexId || v.ticker)
}

export function computeRestoreFilterOn(filterType, value) {
  let filterOn

  forEach(filterType, item => {
    const options = find(item.options, { value })
    if (options) {
      filterOn = options
      return false
    }
  })

  return filterOn
}

export const NUMERIC_OPERATORS = [{
  label: "is greater than",
  value: "gt",
  queryFormatter: querySingleFloatFormatter,
  validator: singleNumberValidator,
}, {
  label: "is greater than or equal to",
  value: "gte",
  queryFormatter: querySingleFloatFormatter,
  validator: singleNumberValidator,
}, {
  label: "is less than",
  value: "lt",
  queryFormatter: querySingleFloatFormatter,
  validator: singleNumberValidator,
}, {
  label: "is less than or equal to",
  value: "lte",
  queryFormatter: querySingleFloatFormatter,
  validator: singleNumberValidator,
}, {
  label: "equals",
  value: "eq",
  queryFormatter: querySingleFloatFormatter,
  validator: singleNumberValidator,
}, {
  label: "does not equal",
  value: "neq",
  queryFormatter: querySingleFloatFormatter,
  validator: singleNumberValidator,
}, {
  label: "is between",
  value: "between",
  queryFormatter: queryRangeFloatFormatter,
  selectorType: "range",
  validator: rangeNumberValidator,
}, {
  label: "is not between",
  value: "notBetween",
  queryFormatter: queryRangeFloatFormatter,
  selectorType: "range",
  validator: rangeNumberValidator,
}]

export const NUMERIC_AND_BOOL_OPERATORS = [
  ...NUMERIC_OPERATORS,
  {
    label: "has value",
    value: "hasValue",
    queryFormatter: fixedValueFormatter(true),
    validator: alwaysValid,
    selectorType: "none",
  },
  {
    label: "has no value",
    value: "hasNoValue",
    queryFormatter: fixedValueFormatter(true),
    validator: alwaysValid,
    selectorType: "none",
  },
]

export const STRING_OPERATORS = [{
  label: "is",
  value: "eq",
  queryFormatter: querySingleStringFormatter,
  selectorType: "singleSelect",
  validator: descriptorValidator,
}, {
  label: "is not",
  value: "neq",
  queryFormatter: querySingleStringFormatter,
  selectorType: "singleSelect",
  validator: descriptorValidator,
}, {
  label: "is in",
  value: "in",
  queryFormatter: queryMultiStringFormatter,
  selectorType: "multiSelect",
  validator: descriptorValidator,
}, {
  label: "is not in",
  value: "notIn",
  queryFormatter: queryMultiStringFormatter,
  selectorType: "multiSelect",
  validator: descriptorValidator,
}]

export const CLASSIFICATION_OPERATORS = [{
  label: "is",
  value: "eq",
  queryFormatter: querySingleClassificationFormatter,
  selectorType: "singleSelect",
  validator: descriptorValidator,
}, {
  label: "is not",
  value: "neq",
  queryFormatter: querySingleClassificationFormatter,
  selectorType: "singleSelect",
  validator: descriptorValidator,
}, {
  label: "is in",
  value: "in",
  queryFormatter: queryMultiClassificationFormatter,
  selectorType: "multiSelect",
  validator: descriptorValidator,
}, {
  label: "is not in",
  value: "notIn",
  queryFormatter: queryMultiClassificationFormatter,
  selectorType: "multiSelect",
  validator: descriptorValidator,
}]

export const MEMBERSHIP_OPERATORS = [{
  label: "is",
  value: "eq",
  selectorType: "singleSelect",
  queryFormatter: querySingleMembershipFormatter,
  validator: descriptorValidator,
}, {
  label: "is not",
  value: "neq",
  queryFormatter: querySingleMembershipFormatter,
  selectorType: "singleSelect",
  validator: descriptorValidator,
}, {
  label: "is in",
  value: "in",
  queryFormatter: queryMultiMembershipFormatter,
  selectorType: "multiSelect",
  validator: descriptorValidator,
}, {
  label: "is not in",
  value: "notIn",
  queryFormatter: queryMultiMembershipFormatter,
  selectorType: "multiSelect",
  validator: descriptorValidator,
}]

export const SECURITIES_OPERATORS = [{
  label: "is in",
  value: "in",
  queryFormatter: noop,
  selectorType: "securitiesSearch",
  validator: securityValidator,
}, {
  label: "is not in",
  value: "notIn",
  queryFormatter: noop,
  selectorType: "securitiesSearch",
  validator: securityValidator,
}]

const disableMembershipFilter = (currentFilter, type, categoryId) => chain(currentFilter)
  .get(type)
  .find(filter => filter.id === categoryId)
  .value()

const appendMembershipFilters = (filtersArray, { currentFilter, editing, filters, categories, isDisabled, tooltip, showAdjustmentIcon, filterMeta: { type, label }, isUnsupported = false }) => {
  const options = chain(categories)
    .map(({ id, name, isDisabled }) => {
      if (size(filters[id]) === 0) {
        return null
      }
      return {
        label: name,
        value: id,
        dataType: "string",
        type,
        operators: MEMBERSHIP_OPERATORS,
        tooltip,
        isDisabled: isDisabled || (disableMembershipFilter(currentFilter, type, id) && !editing),
        isUnsupported,
        options: map(filters[id], option => ({
          ...option,
          type,
          dataType: "string",
        })),
      }
    })
    .compact()
    .value()

  if (size(options)) {
    filtersArray.push({
      label,
      type,
      isDisabled,
      tooltip,
      showAdjustmentIcon,
      options,
      isUnsupported,
    })
  }
}

export const computeFilterOns = (
  viewType,
  portfolioFilters,
  currentFilter,
  editing,
  tagCategories,
  classifications,
  classificationValues,
  attributeCategories,
  assetType,
  normalization,
  hasMultipleInstruments) => {
  const filters = []
  if (!portfolioFilters) {
    return filters
  }

  const {
    long,
    short,
    countries,
    currencies,
    assetClasses,
    assetSubclasses,
    classifications: classificationFilters,
    tags: tagFilters,
    positionAttributes: attributeFilters,
  } = portfolioFilters

  if (long && short) {
    filters.push({
      label: "Long/Short",
      type: "longshort",
      options: [{
        label: "Long",
        type: "longshort",
        value: "LONG",
        dataType: "constant",
        isDisabled: !!get(currentFilter, "longShort") && !editing,
      }, {
        label: "Short",
        type: "longshort",
        value: "SHORT",
        dataType: "constant",
        isDisabled: !!get(currentFilter, "longShort") && !editing,
      }],
    })
  }

  const descriptorsOptions = []

  if (size(countries) > 1) {
    descriptorsOptions.push({
      label: "Country",
      type: "descriptor",
      dataType: "string",
      operators: STRING_OPERATORS,
      value: "country",
      isDisabled: !!get(currentFilter, "country") && !editing,
      options: countries,
    })
  }

  if (size(currencies) > 1) {
    descriptorsOptions.push({
      label: "Currency",
      type: "descriptor",
      dataType: "string",
      operators: STRING_OPERATORS,
      value: "currency",
      isDisabled: !!get(currentFilter, "currency") && !editing,
      options: currencies,
    })
  }

  if (size(assetClasses) > 1) {
    descriptorsOptions.push({
      label: "Asset Class",
      type: "descriptor",
      dataType: "string",
      operators: STRING_OPERATORS,
      value: "assetClass",
      isDisabled: !!get(currentFilter, "assetClass") && !editing,
      options: assetClasses,
    })
  }

  if (size(assetSubclasses) > 1) {
    descriptorsOptions.push({
      label: "Asset Subclass",
      type: "descriptor",
      dataType: "string",
      operators: STRING_OPERATORS,
      value: "assetSubclass",
      isDisabled: !!get(currentFilter, "assetSubclass") && !editing,
      options: assetSubclasses,
    })
  }

  descriptorsOptions.push({
    label: "Average Daily Volume",
    type: "descriptor",
    dataType: "millions",
    operators: NUMERIC_OPERATORS,
    value: "averageDailyVolume",
    isDisabled: !!get(currentFilter, "averageDailyVolume") && !editing,
  }, {
    label: "Market Cap",
    type: "descriptor",
    dataType: "millions",
    operators: NUMERIC_OPERATORS,
    value: "marketCapitalization",
    isDisabled: !!get(currentFilter, "marketCapitalization") && !editing,
  })

  filters.push({
    label: "Descriptors",
    type: "descriptor",
    options: descriptorsOptions,
  })

  if (size(classificationFilters) > 0 && size(classifications) > 0) {
    const options = []

    for (const classification of classifications) {
      const versionValues = get(classificationValues, `${classification.id}.version.values`)
      const tiers = classification.version.tiers
      const allClassificationValues = flattenClassificationValues(tiers, versionValues, 1)
      options.push({
        label: `${classification.name}`,
        isDisabled: chain(currentFilter)
          .get("classification")
          .some(filter => filter.id === classification.id)
          .value(),
        type: "classification",
        dataType: "string",
        operators: CLASSIFICATION_OPERATORS,
        options: map(classificationFilters[classification.id], v => ({
          value: v.value,
          tierId: chain(allClassificationValues).find(a => a.value === v.value).get("tierId").value(),
          label: `${v.label} - ${v.value}`,
        })),
        value: classification.id,
      })
    }

    filters.push({
      label: "Classification",
      type: "classification",
      options,
    })
  }

  filters.push({
    label: "Securities",
    type: "securities",
    options: [{
      label: `${toUpper(assetType) === "ETF" ? "ETF" : (capitalize(assetType) || "Portfolio")} Securities`,
      isDisabled: !!get(currentFilter, "securities") && !editing,
      type: "securities",
      dataType: "string",
      operators: SECURITIES_OPERATORS,
      value: "securities",
    }],
  })

  const isTagFilterSupported = viewType !== VIEW_TYPES.ACTIVE || normalization === NORMALIZATION.STANDALONE
  const isDisabled = !isTagFilterSupported || hasMultipleInstruments

  // TODO: [APP-1404] Remove isDisabled completely once all components are updated to latest filter UI
  if (!isEmpty(tagFilters) && !isEmpty(tagCategories)) {
    const tooltip = hasMultipleInstruments ? "Filtering on tags is not allowed in dashboard with multiple instruments." : "Filtering on tags is not allowed in contribution mode when active."
    appendMembershipFilters(filters, {
      currentFilter,
      editing,
      filters: tagFilters,
      isDisabled,
      isUnsupported: isDisabled,
      tooltip: isDisabled && tooltip,
      showAdjustmentIcon: isDisabled,
      categories: map(tagCategories, category => ({ ...category, isDisabled })),
      filterMeta: { type: "tag", label: "Tags" },
    })
  }

  if (!isEmpty(attributeFilters) && !isEmpty(attributeCategories)) {
    const tooltip = hasMultipleInstruments ? "Filtering on position set attributes is not allowed in dashboard with multiple instruments." : "Filtering on position set attributes is not allowed in contribution mode when active."
    appendMembershipFilters(filters, {
      currentFilter,
      editing,
      filters: attributeFilters,
      isDisabled,
      isUnsupported: isDisabled,
      tooltip: isDisabled && tooltip,
      showAdjustmentIcon: isDisabled,
      categories: map(attributeCategories, category => ({ ...category, isDisabled })),
      filterMeta: { type: "positionAttributes", label: "Position Attributes" },
    })
  }

  return filters
}
